/**
 * Modo painel — a unidade numa tela pendurada na parede.
 *
 * SÃO DOIS PAINÉIS, e a divisão de trabalho é esta:
 *
 *   • `KioskTenant` — todos os ATIVOS do cliente, um cartão cada, de todas
 *     as unidades. É a tela de quem acompanha a operação inteira.
 *   • esta — UMA unidade: os agregados dela à esquerda e a planta à
 *     direita. É a tela pendurada DENTRO da unidade, onde a pergunta não é
 *     "qual ativo" (são poucos e todo mundo sabe quais) e sim ONDE e COMO
 *     está indo.
 *
 * Já tentei fundir as duas — um cartão por ativo aqui também — e ficou
 * pior: a planta, que é o que só esta tela tem, virou metade de uma tela
 * disputada.
 *
 * É outro produto, não a mesma tela em fullscreen. Quem lê está a três metros
 * de distância, não tem mouse, e a página vai ficar aberta por semanas.
 *
 *  • CABE NUMA TELA, SEM ROLAGEM. Esta é a regra que manda em todo o resto, e
 *    é o motivo de a tela não reaproveitar o `TenantStatsCard`: aquele card é
 *    de página que rola — gráficos, seletor de período, colapso — e sozinho
 *    ele ocupava a altura inteira, empurrando a planta pra baixo da dobra.
 *    Numa TV pendurada, o que está abaixo da dobra não existe: ninguém rola
 *    uma parede.
 *
 *    Daí o layout em duas colunas: os números numa coluna estreita à
 *    esquerda, a planta ocupando todo o resto. A planta é o que justifica
 *    esta tela; os números são contexto.
 *
 *  • SEM NAVEGAÇÃO E SEM CONTROLE. Nada pra clicar é uma decisão, não uma
 *    omissão: numa tela pública, todo controle é um jeito de alguém deixar o
 *    painel num estado do qual ninguém vai tirar. Inclusive o seletor de
 *    período — aqui ele é fixo em 24 h.
 *
 *  • O RELÓGIO DE FRESCOR É O ELEMENTO MAIS IMPORTANTE DA TELA. Painel
 *    congelado mostrando tudo verde é pior que painel apagado — o apagado
 *    ninguém acredita. Ver `RelogioDoPainel`.
 *
 *  • CORES DO TEMA. Fundo branco fixo numa tela de parede é lanterna, e o
 *    app já tem tema escuro — herdar é mais correto e mais barato que
 *    inventar uma paleta só pra cá.
 *
 *  • A SESSÃO NÃO CAI ENQUANTO A ABA ESTIVER ABERTA. O token vale 24 h, e
 *    antes isto mandava a tela de parede pra página de login uma vez por
 *    dia. Agora o `AuthProvider` renova o token na metade da vida dele, então
 *    a sessão dura o que a tela durar. Fechar a aba ou reiniciar a máquina
 *    ainda pedem login — ver `deploy/MODO_PAINEL.md`.
 */

import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { DoorOpen, Thermometer, Wifi, Zap } from "lucide-react";

import { PlantaDoSite } from "@/components/PlantaDoSite";
import { ESTADO, estadoDoAtivo, piorEstado } from "@/lib/estadoDoAtivo";
import { RelogioDoPainel } from "@/components/RelogioDoPainel";
import { SairDoPainel } from "@/components/SairDoPainel";
import {
  useActiveAlerts, useEquipmentsInSite, useSites, useTenantStats,
} from "@/hooks/queries";
import type { ActiveAlert, TenantStats } from "@/types";

/** Período fixo do painel. Sem seletor: ver "sem controle" no topo. */
const JANELA_H = 24;

function umNumero(valor: number | null | undefined, casas = 1): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return "—";
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });
}

export default function SiteKioskPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const sites = useSites();
  const site = sites.data?.find((s) => s.id === siteId);
  const ativos = useEquipmentsInSite(siteId);
  const alertas = useActiveAlerts();

  const janela = useMemo(() => {
    const ate = new Date();
    const de = new Date(ate.getTime() - JANELA_H * 3600_000);
    return { from: de.toISOString(), to: ate.toISOString() };
  }, []);

  const stats = useTenantStats(site?.tenant_id, { ...janela, site_id: siteId });

  // Os alertas desta unidade. A tela tinha quatro números e nenhuma
  // resposta pra "tem algo errado agora" — que é a primeira coisa que
  // alguém pergunta olhando uma parede.
  const alertasDaUnidade = useMemo(() => {
    const daUnidade = new Set(
      (ativos.data ?? []).filter((e) => e.active).map((e) => e.id));
    return (alertas.data ?? []).filter((a) => daUnidade.has(a.equipment_id));
  }, [alertas.data, ativos.data]);

  if (!siteId) return null;

  return (
    // `h-dvh` + `overflow-hidden`: a tela é do tamanho da tela, e o que não
    // couber precisa APARECER cortado no desenvolvimento, não virar rolagem
    // silenciosa que ninguém na parede vai executar.
    <div className="borda-segura flex h-dvh flex-col overflow-hidden bg-surface-50 dark:bg-slate-950">
      <header className="mb-4 flex shrink-0 flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            {site?.name ?? "Unidade"}
          </h1>
          {site?.address && (
            <p className="text-sm text-slate-500 dark:text-slate-400">{site.address}</p>
          )}
        </div>
        <div className="flex items-start gap-3">
          {/* A tela da unidade não existe mais — a saída vai pro dashboard,
              que é de onde se abre este painel. */}
          <SairDoPainel para="/" />
          <RelogioDoPainel marca={alertas.dataUpdatedAt} />
        </div>
      </header>

      {/* Duas colunas: números à esquerda, planta ocupando o resto. Abaixo de
          1024px vira uma coluna só — não é o caso de uso, mas a tela não pode
          quebrar num monitor de pé. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-[minmax(240px,20%)_1fr]">
        <div className="flex min-h-0 flex-col gap-3 overflow-hidden">
          <EstadoDaUnidade alertas={alertasDaUnidade} />
          <IndicadoresDaUnidade dados={stats.data} />
        </div>

        {/* Sem `editavel` e sem `colapsavel`: na parede não há quem arraste
            nem quem recolha, e um painel recolhido por engano fica assim por
            dias. `ajustarNaAltura` é o que mantém tudo numa tela só. */}
        <div className="min-h-0">
          <PlantaDoSite siteId={siteId} ajustarNaAltura navegavel={false} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A coluna de números.
 *
 * Quatro tiles, sem gráfico. Gráfico de três metros é mancha: a curva de
 * temperatura só se lê de perto, e quem lê de perto abre o app. O que a
 * parede precisa responder é "está dentro?", e isso é um número.
 */
function IndicadoresDaUnidade({ dados }: { dados: TenantStats | undefined }) {
  const t = dados?.temperature;
  const e = dados?.energy;
  const p = dados?.door;
  const u = dados?.uptime;

  return (
    <>
      <Tile
        Icone={Thermometer}
        rotulo="Temperatura média"
        valor={t?.avg !== null && t?.avg !== undefined ? `${umNumero(t.avg)} °C` : "—"}
        apoio={
          t && t.min !== null && t.max !== null
            ? `${umNumero(t.min)} a ${umNumero(t.max)} °C`
            : undefined
        }
      />
      <Tile
        Icone={Wifi}
        rotulo="Disponibilidade"
        valor={u?.online_pct !== null && u?.online_pct !== undefined
          ? `${umNumero(u.online_pct)}%` : "—"}
        apoio={t?.sli_pct !== null && t?.sli_pct !== undefined
          ? `${umNumero(t.sli_pct)}% do tempo na faixa` : undefined}
      />
      <Tile
        Icone={DoorOpen}
        rotulo="Aberturas"
        valor={p ? String(p.open_count) : "—"}
        apoio={p ? `${Math.round(p.total_open_seconds / 60)} min abertas` : undefined}
      />
      <Tile
        Icone={Zap}
        rotulo="Consumo"
        valor={e ? `${umNumero(e.total_kwh, 2)} kWh` : "—"}
        apoio={e?.instant_w !== null && e?.instant_w !== undefined
          ? `${umNumero(e.instant_w, 0)} W agora` : undefined}
      />
      <p className="mt-auto shrink-0 text-xs text-slate-400 dark:text-slate-500">
        últimas {JANELA_H} horas
      </p>
    </>
  );
}

/**
 * O estado dos alertas da unidade, no topo da coluna.
 *
 * Mesma linguagem do painel do cliente: disco colorido, ícone e palavra. E
 * quando não há alerta ele AFIRMA isso em vez de sumir — bloco ausente é
 * ambíguo entre "está tudo bem" e "não carregou", e quem passa na frente
 * não tem como desempatar.
 */
function EstadoDaUnidade({ alertas }: { alertas: ActiveAlert[] }) {
  const estado = alertas.length
    ? piorEstado(alertas.map((a) => estadoDoAtivo([a])))
    : "ok";
  const { rotulo, cor, Icone } = ESTADO[estado];

  return (
    <div className="flex shrink-0 items-center gap-3 rounded-xl border border-surface-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
        style={{ background: cor }}
      >
        <Icone className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <div className="truncate text-lg font-semibold text-slate-900 dark:text-slate-100">
          {alertas.length === 0
            ? "Sem alertas"
            : `${alertas.length} ${alertas.length === 1 ? "alerta" : "alertas"}`}
        </div>
        <div className="truncate text-xs text-slate-500 dark:text-slate-400">
          {alertas.length === 0 ? "Tudo dentro do previsto" : rotulo}
        </div>
      </div>
    </div>
  );
}

/**
 * Um indicador.
 *
 * O ícone vai num disco, como no painel do cliente: quatro caixas cinzas com
 * um ícone cinza dentro não se distinguem de três metros, e a tela inteira
 * vira um bloco uniforme. O disco dá âncora visual pra cada número sem
 * acrescentar cor de ESTADO onde não há estado — estes são agregados do
 * período, não veredito sobre o agora, e pintá-los de verde ou vermelho
 * afirmaria algo que o número não diz.
 */
function Tile({
  Icone, rotulo, valor, apoio,
}: {
  Icone: typeof Thermometer;
  rotulo: string;
  valor: string;
  apoio?: string;
}) {
  return (
    <div className="flex min-h-0 items-center gap-3 rounded-xl border border-surface-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">
        <Icone className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <div className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {rotulo}
        </div>
        <div className="text-2xl font-semibold tabular-nums leading-tight text-slate-900 dark:text-slate-100">
          {valor}
        </div>
        {apoio && (
          <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
            {apoio}
          </div>
        )}
      </div>
    </div>
  );
}
